import React from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import { formatHours } from '../../utils/numberUtils';

const monthNames = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'
];

const UsersAnalyticsTable = ({ usersData, currentYear }) => {
  if (!usersData || usersData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Нет данных</h3>
        <p className="text-gray-500">
          За {currentYear} год нет завершенных задач в архиве
        </p>
      </div>
    );
  }

  // Sum hours per month across all users (for footer row)
  const sumsByMonth = monthNames.map((_, monthIndex) => ({
    hours: usersData.reduce((sum, u) => sum + (u.monthlyData?.[monthIndex]?.hours ?? 0), 0),
    tasks: usersData.reduce((sum, u) => sum + (u.monthlyData?.[monthIndex]?.tasks ?? 0), 0)
  }));
  const totalHoursAll = sumsByMonth.reduce((s, m) => s + m.hours, 0);
  const totalTasksAll = sumsByMonth.reduce((s, m) => s + m.tasks, 0);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-x-auto">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          Детальная статистика по пользователям за {currentYear} год
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Сравнивайте часы и задачи по месяцам для каждого пользователя
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              <th className="p-3 text-left font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">Пользователь</th>
              {monthNames.map((month) => (
                <th key={month} className="p-3 text-center font-medium text-gray-700 bg-gray-50">{month}</th>
              ))}
              <th className="p-3 text-center font-medium text-gray-700 bg-gray-50">Итого</th>
            </tr>
          </thead>
          <tbody>
            {usersData.map((user, idx) => (
              <tr key={user.userId} className="border-b hover:bg-gray-50">
                {/* Имя пользователя */}
                <td className="p-3 font-semibold text-gray-900 bg-white sticky left-0 z-10 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 text-blue-600" />
                    {user.userName}
                    {idx === 0 && (
                      <span className="ml-2 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Лидер</span>
                    )}
                  </div>
                </td>
                {/* Месяцы */}
                {user.monthlyData.map((month, i) => (
                  <td key={i} className="p-3 text-center align-top">
                    <div className="text-lg font-bold text-gray-900">{formatHours(month.hours)}</div>
                    <div className="text-xs text-gray-500">{month.tasks} задач</div>
                  </td>
                ))}
                {/* Итого */}
                <td className="p-3 text-center align-top bg-gray-50">
                  <div className="text-lg font-bold text-blue-700">{formatHours(user.totalHours)}</div>
                  <div className="text-xs text-gray-500">{user.totalTasks} задач</div>
                </td>
              </tr>
            ))}
            {/* Итоговая строка: сумма часов за каждый месяц */}
            <tr className="border-t-2 border-gray-300 bg-blue-50 font-semibold">
              <td className="p-3 text-left text-gray-900 sticky left-0 z-10">Сумма за месяц</td>
              {sumsByMonth.map((month, i) => (
                <td key={i} className="p-3 text-center">
                  <div className="text-lg font-bold text-gray-900">{formatHours(month.hours)}</div>
                  <div className="text-xs text-gray-500">{month.tasks} задач</div>
                </td>
              ))}
              <td className="p-3 text-center bg-blue-100">
                <div className="text-lg font-bold text-blue-800">{formatHours(totalHoursAll)}</div>
                <div className="text-xs text-gray-600">{totalTasksAll} задач</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersAnalyticsTable; 